import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FaUpload, FaTrash, FaDownload } from 'react-icons/fa';
import remorqueService from '../../services/remorqueService';
import api from '../../services/api';
import { getFileUrl } from '../../config';
import { useI18n } from '../../i18n/I18nContext';

function RemorqueForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const { t } = useI18n();

    const [formData, setFormData] = useState({
        plateNumber: '',
        brand: '',
        model: '',
        type: '',
        capacity: '',
        status: 'AVAILABLE',
        year: new Date().getFullYear()
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [isUploadingDocs, setIsUploadingDocs] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchRemorque();
            fetchRemorqueDocuments();
        }
    }, [id]);

    const fetchRemorque = async () => {
        try {
            const data = await remorqueService.getRemorque(id);
            setFormData(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchRemorqueDocuments = async () => {
        try {
            const response = await api.get(`/transporteur/remorques/${id}/documents`);
            setExistingDocuments(response.data || []);
        } catch (_) { }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            let savedRemorque;
            if (isEditMode) {
                savedRemorque = await remorqueService.updateRemorque(id, formData);
            } else {
                savedRemorque = await remorqueService.createRemorque(formData);
            }

            const remorqueId = isEditMode ? id : savedRemorque.id;

            // If queued documents, upload them
            if (documents.length) {
                setIsUploadingDocs(true);
                for (const doc of documents) {
                    const fd = new FormData();
                    fd.append('file', doc.file);
                    if (doc.name) fd.append('name', doc.name);
                    if (doc.type) fd.append('type', doc.type);
                    await api.post(`/transporteur/remorques/${remorqueId}/documents`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
                setDocuments([]);
                if (isEditMode) {
                    await fetchRemorqueDocuments();
                }
                setIsUploadingDocs(false);
            }

            setSuccess(true);
            setTimeout(() => {
                navigate('/remorques');
            }, 1500);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const onFilesSelected = (files) => {
        const MAX_FILE_MB = 25;
        const rejected = [];
        const accepted = [];
        Array.from(files).forEach(f => {
            const sizeMb = f.size / (1024 * 1024);
            if (sizeMb > MAX_FILE_MB) {
                rejected.push({ name: f.name, sizeMb: sizeMb.toFixed(1) });
            } else {
                accepted.push({ file: f, name: f.name, type: 'carte_grise' });
            }
        });
        if (rejected.length) {
            setError(t('common.error') + `: ${rejected.map(r => r.name + ' (' + r.sizeMb + 'MB)').join(', ')}`);
        }
        if (accepted.length) {
            setDocuments(prev => [...prev, ...accepted]);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length) {
            onFilesSelected(e.target.files);
            e.target.value = '';
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length) {
            onFilesSelected(e.dataTransfer.files);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const updateDocumentMeta = (index, key, value) => {
        setDocuments(prev => prev.map((doc, i) => i === index ? { ...doc, [key]: value } : doc));
    };

    const removeDocument = (index) => {
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <DashboardLayout>
            <div className="container mt-4 pb-5">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="card shadow-sm" style={{ borderRadius: '1rem' }}>
                            <div className="card-body p-4">
                                <h2 className="card-title h4 mb-4 fw-bold">
                                    {isEditMode ? t('remorques.editTitle') : t('remorques.addTitle')}
                                </h2>

                                {error && (
                                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                                        <i className="bi bi-exclamation-circle-fill me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {success && (
                                    <div className="alert alert-success d-flex align-items-center" role="alert">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        {isEditMode ? t('remorques.updatedSuccess') : t('remorques.addedSuccess')}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.plateNumber')} *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="plateNumber"
                                                value={formData.plateNumber}
                                                onChange={handleChange}
                                                placeholder={t('remorques.form.plateNumber')}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.type')} *</label>
                                            <select
                                                className="form-select"
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">{t('remorques.form.selectType')}</option>
                                                <option value="PLATEAU">{t('remorques.types.plateau')}</option>
                                                <option value="BENNE">{t('remorques.types.benne')}</option>
                                                <option value="CITERNE">{t('remorques.types.citerne')}</option>
                                                <option value="FRIGO">{t('remorques.types.frigo')}</option>
                                                <option value="TAUTLINER">{t('remorques.types.tautliner')}</option>
                                                <option value="PORTE_ENGIN">{t('remorques.types.porte_engin')}</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.brand')} *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="brand"
                                                value={formData.brand}
                                                onChange={handleChange}
                                                placeholder={t('remorques.form.brand')}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.model')} *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="model"
                                                value={formData.model}
                                                onChange={handleChange}
                                                placeholder={t('remorques.form.model')}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.year')} *</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="year"
                                                value={formData.year}
                                                onChange={handleChange}
                                                min="1990"
                                                max={new Date().getFullYear() + 1}
                                                placeholder={t('remorques.form.year')}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.capacity')} *</label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="capacity"
                                                    value={formData.capacity}
                                                    onChange={handleChange}
                                                    min="0"
                                                    step="0.1"
                                                    placeholder={t('remorques.form.capacity')}
                                                    required
                                                />
                                                <span className="input-group-text">t</span>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small text-muted text-uppercase fw-bold">{t('remorques.form.status')} *</label>
                                            <select
                                                className="form-select"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="AVAILABLE">{t('remorques.status.available')}</option>
                                                <option value="OUT_OF_SERVICE">{t('remorques.status.unavailable')}</option>
                                                <option value="MAINTENANCE">{t('remorques.status.inMaintenance')}</option>
                                                <option value="IN_USE">{t('remorques.status.inUse')}</option>
                                            </select>
                                        </div>

                                        <div className="col-12 mt-5">
                                            <hr className="text-muted opacity-25" />
                                            <h3 className="h6 fw-bold mb-3 text-uppercase small text-muted">{t('remorques.documents.title')}</h3>
                                            <div
                                                className="dropzone p-4 bg-light border border-2 border-dashed rounded-3 text-center"
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                            >
                                                <input
                                                    id="remorqueFileInput"
                                                    type="file"
                                                    multiple
                                                    className="d-none"
                                                    onChange={handleFileInputChange}
                                                />
                                                <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-2 d-block"></i>
                                                <label htmlFor="remorqueFileInput" className="btn btn-sm btn-primary mb-2">
                                                    {t('remorques.documents.browse')}
                                                </label>
                                                <p className="text-muted small mb-0">{t('remorques.documents.dragDrop')}</p>
                                            </div>

                                            {documents.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="small fw-bold text-muted mb-2">{t('remorques.documents.queued')}:</p>
                                                    {documents.map((d, idx) => (
                                                        <div key={idx} className="card p-2 mb-2 bg-light border-0">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className="bi bi-file-earmark-pdf text-danger"></i>
                                                                    <span className="small text-truncate" style={{ maxWidth: '12.5rem' }}>{d.file.name}</span>
                                                                </div>
                                                                <button type="button" className="btn btn-sm text-danger" onClick={() => removeDocument(idx)}>
                                                                    <i className="bi bi-x-lg"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {existingDocuments.length > 0 && (
                                                <div className="mt-4">
                                                    <p className="small fw-bold text-muted mb-2">{t('remorques.documents.existing')}:</p>
                                                    <div className="row g-2">
                                                        {existingDocuments.map(doc => (
                                                            <div key={doc.id} className="col-md-6">
                                                                <div className="card p-2 border shadow-none h-100">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <i className="bi bi-file-earmark-check text-success"></i>
                                                                            <span className="small text-truncate" style={{ maxWidth: '7.5rem' }}>{doc.name || 'Document'}</span>
                                                                        </div>
                                                                        <div className="d-flex gap-1">
                                                                            <a href={getFileUrl(doc.fileUrl)} target="_blank" rel="noreferrer" className="btn btn-sm btn-link p-0">
                                                                                <i className="bi bi-eye"></i>
                                                                            </a>
                                                                            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={async () => {
                                                                                if (window.confirm(t('common.confirmDelete') || 'Delete this document?')) {
                                                                                    try {
                                                                                        await api.delete(`/transporteur/documents/${doc.id}`);
                                                                                        setExistingDocuments(prev => prev.filter(d => d.id !== doc.id));
                                                                                    } catch (_) { }
                                                                                }
                                                                            }}>
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-12 mt-5">
                                            <div className="d-flex gap-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4 py-2"
                                                    disabled={loading || isUploadingDocs}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t('common.loading')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check2-circle me-2"></i>
                                                            {isEditMode ? t('remorques.updateButton') : t('remorques.addButton')}
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary px-4 py-2"
                                                    onClick={() => navigate('/remorques')}
                                                >
                                                    {t('common.close')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default RemorqueForm;
